import * as React from 'react';
import { useState } from 'react';
import {
    LockOutlined,
    UserOutlined,
    EyeInvisibleOutlined,
    EyeTwoTone,
    InfoCircleOutlined,
    CloseCircleOutlined,
    DisconnectOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, message } from 'antd';
import { useNavigate } from 'react-router';
import Cookies from 'js-cookie';
import classNames from 'classnames/bind';
import styles from './Login.module.scss';
import * as AuthService from '../../services/authService';
import { AUTH } from '../../utils/constant';

const cx = classNames.bind(styles);

export default function Login() {
    const navigate = useNavigate();
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [loadingLogin, setLoadingLogin] = useState(false);

    const onFinish = async (values) => {
        setLoadingLogin(true);
        try {
            const result = await AuthService.login({
                username: values.username,
                password: values.password,
            });

            if (result?.statusCode === 200) {
                localStorage.setItem('expiresIn', result?.data?.token?.expiresIn);
                Cookies.set('accessToken', result?.data?.token?.accessToken, {
                    expires: AUTH.EXPIRE_ACCESS_TOKEN,
                    secure: true,
                    sameSite: 'Strict',
                });
                Cookies.set('refreshToken', result?.data?.token?.refreshToken, {
                    expires: AUTH.EXPIRE_REFRESH_TOKEN,
                    secure: true,
                    sameSite: 'Strict',
                });

                const { userRole, ...userInfoWithoutRole } = result?.data?.userInfo;
                localStorage.setItem('userInfo', JSON.stringify(userInfoWithoutRole));
                navigate('/folder');
            } else {
                handleLoginError(result);
            }
        } catch (error) {
            console.error('error', error);
            message.error('Đã xảy ra lỗi không xác định');
        }
        setLoadingLogin(false);
    };

    const handleLoginError = (result) => {
        if (result?.statusCode === 400) {
            if (result?.message === 'Account has been banned') {
                message.error({
                    content: (
                        <div style={{ padding: '8px 0' }}>
                            <div style={{ marginBottom: '8px', fontWeight: 500 }}>Tài khoản đã bị khóa</div>
                            <div style={{ fontSize: '13px', color: 'rgba(0, 0, 0, 0.65)' }}>
                                Tài khoản của bạn đã bị khóa do vi phạm điều khoản sử dụng. Vui lòng liên hệ với quản
                                trị viên để được hỗ trợ.
                            </div>
                            <Button
                                type="link"
                                size="small"
                                style={{ padding: '4px 0', height: 'auto', marginTop: '8px' }}
                                onClick={() => (window.location.href = '/contact-support')}
                            >
                                Liên hệ hỗ trợ
                            </Button>
                        </div>
                    ),
                    icon: <LockOutlined style={{ color: '#ff4d4f' }} />,
                    duration: 5,
                });
            } else if (result?.message === 'Account not activated') {
                message.warning({
                    content: (
                        <div style={{ padding: '8px 0' }}>
                            <div style={{ marginBottom: '8px', fontWeight: 500 }}>Tài khoản chưa kích hoạt</div>
                            <div style={{ fontSize: '13px', color: 'rgba(0, 0, 0, 0.65)' }}>
                                Vui lòng kiểm tra email và nhập mã OTP để hoàn tất quá trình đăng ký. Sau đó thử đăng
                                nhập lại.
                            </div>
                        </div>
                    ),
                    icon: <InfoCircleOutlined style={{ color: '#faad14' }} />,
                    duration: 5,
                });
            }
        } else if (result?.statusCode === 404 && result?.message === 'account not found') {
            message.error({
                content: (
                    <div style={{ padding: '4px 0' }}>
                        <div style={{ fontWeight: 500 }}>Tài khoản không tồn tại</div>
                        <div style={{ fontSize: '13px', color: 'rgba(0, 0, 0, 0.65)' }}>
                            Email này chưa được đăng ký. Vui lòng đăng ký tài khoản mới.
                        </div>
                    </div>
                ),
                icon: <UserOutlined style={{ color: '#ff4d4f' }} />,
                duration: 5,
            });
        } else if (result?.statusCode === 401) {
            message.error({
                content: (
                    <div style={{ padding: '4px 0' }}>
                        <div style={{ fontWeight: 500 }}>Đăng nhập không thành công</div>
                        <div style={{ fontSize: '13px', color: 'rgba(0, 0, 0, 0.65)' }}>
                            Email hoặc mật khẩu không chính xác
                        </div>
                    </div>
                ),
                icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
            });
        } else if (result?.statusCode === 'NETWORK_ERROR') {
            message.error({
                content: (
                    <div style={{ padding: '4px 0' }}>
                        <div style={{ fontWeight: 500 }}>Lỗi kết nối</div>
                        <div style={{ fontSize: '13px', color: 'rgba(0, 0, 0, 0.65)' }}>
                            Vui lòng kiểm tra kết nối mạng và thử lại
                        </div>
                    </div>
                ),
                icon: <DisconnectOutlined style={{ color: '#ff4d4f' }} />,
            });
        } else {
            message.error({
                content: result?.statusText || 'Có lỗi xảy ra, vui lòng thử lại sau',
                icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
            });
        }
    };

    return (
        <div className={cx('wrap')}>
            <div className={cx('background-image')}>
                <div className={cx('background-left')}>
                    <div className={cx('shapes')}>
                        <div className={cx('circle', 'circle-1')}></div>
                        <div className={cx('circle', 'circle-2')}></div>
                        <div className={cx('dot', 'dot-1')}></div>
                        <div className={cx('dot', 'dot-2')}></div>
                        <div className={cx('dot', 'dot-3')}></div>
                    </div>
                </div>
                <div className={cx('background-right')}>
                    <div className={cx('shapes')}>
                        <div className={cx('circle', 'circle-3')}></div>
                        <div className={cx('circle', 'circle-4')}></div>
                        <div className={cx('dot', 'dot-4')}></div>
                        <div className={cx('dot', 'dot-5')}></div>
                    </div>
                </div>
            </div>

            <div className={cx('login-container')}>
                <div className={cx('welcome-section')}>
                    <h1 className={cx('welcome-title')}>Chào mừng bạn đến với trang Portfolio của tôi</h1>
                    <p className={cx('welcome-text')}>Khám phá năng lực, dự án và kinh nghiệm làm việc của tôi.</p>
                    <div className={cx('feature-list')}>
                        <div className={cx('feature-item')}>Thiết kế thân thiện, dễ sử dụng</div>
                        <div className={cx('feature-item')}>Bảo mật thông tin cá nhân</div>
                        <div className={cx('feature-item')}>Cập nhật thông tin nhanh chóng</div>
                    </div>
                </div>

                <div className={cx('form-section')}>
                    <h2 className={cx('form-title')}>Đăng nhập</h2>
                    <Form name="login_form" onFinish={onFinish}>
                        <div className={cx('form-group')}>
                            <label className={cx('label')}>Tài khoản</label>
                            <Form.Item
                                name="username"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập tài khoản!' },
                                    { type: 'email', message: 'Địa chỉ email không hợp lệ!' },
                                ]}
                            >
                                <div className={cx('input-wrapper')}>
                                    <Input
                                        prefix={<UserOutlined className={cx('icon')} />}
                                        placeholder="Nhập email của bạn"
                                        className={cx('form-input')}
                                    />
                                </div>
                            </Form.Item>
                        </div>

                        <div className={cx('form-group')}>
                            <label className={cx('label')}>Mật khẩu</label>
                            <Form.Item name="password" rules={[{ required: true, message: 'Vui lòng nhập mật khẩu!' }]}>
                                <div className={cx('input-wrapper')}>
                                    <Input
                                        prefix={<LockOutlined className={cx('icon')} />}
                                        type={passwordVisible ? 'text' : 'password'}
                                        placeholder="Nhập mật khẩu"
                                        className={cx('form-input')}
                                        suffix={
                                            passwordVisible ? (
                                                <EyeTwoTone
                                                    className={cx('icon')}
                                                    onClick={() => setPasswordVisible(false)}
                                                />
                                            ) : (
                                                <EyeInvisibleOutlined
                                                    className={cx('icon')}
                                                    onClick={() => setPasswordVisible(true)}
                                                />
                                            )
                                        }
                                    />
                                </div>
                            </Form.Item>
                        </div>

                        <div className={cx('action-links')}>
                            <span className={cx('link')} onClick={() => navigate('/register')}>
                                Đăng ký
                            </span>
                            <span className={cx('link')} onClick={() => navigate('/forgot-password')}>
                                Quên mật khẩu
                            </span>
                        </div>

                        <Button type="primary" htmlType="submit" className={cx('submit-button')} loading={loadingLogin}>
                            Đăng nhập
                        </Button>
                    </Form>
                </div>
            </div>
        </div>
    );
}
